import React from 'react';
import { Box, Button } from '@material-ui/core';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Alert, CircularProgress } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import TextField from '@mui/material/TextField';

import { ModalTitle, ModalTypography } from 'components/Modal/Modal';
import { ModalStep } from 'components/Flow/components/ModalStep';
import { DeedHeartHands } from 'components/Flow/components/DeedHeartHands';
import { StepComponentProps } from 'components/Flow/RenderMachine';
import { FilesUpload } from 'containers/FilesUpload/FilesUpload';
import { selectOrganization } from 'entities/user/selectors';

type FormData = {
  legalName: string | null;
  governmentInformationLetterUri: string | null;
  letterOfAssociationUri?: string | null;
};

export const Gov = ({ isTransitionAllowed, send, state }: StepComponentProps): JSX.Element => {
  const organization = useSelector(selectOrganization);

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      legalName: organization?.legalName || null,
      governmentInformationLetterUri: organization?.governmentInformationLetter?.fileUri || null,
      letterOfAssociationUri: organization?.letterOfAssociation?.fileUri || null,
    },
  });

  const watchFields = watch(['governmentInformationLetterUri', 'legalName']);
  const isFormValid = watchFields.every((field) => field?.trim());

  const governmentInformationLetterUploadState =
    typeof state.value === 'object' &&
    'non501c3Gov' in state.value &&
    typeof state.value.non501c3Gov === 'object' &&
    'governmentInformationLetterUpload' in state.value.non501c3Gov &&
    state.value.non501c3Gov.governmentInformationLetterUpload;

  const isSubmitting = governmentInformationLetterUploadState === 'submitting';
  const isFailed = governmentInformationLetterUploadState === 'failure';

  const onSubmit = (data: FormData) => {
    send({
      type: 'PERSIST_LETTER',
      ...data,
    });
  };

  return (
    <ModalStep
      showProgressBar
      actions={
        <div>
          {isTransitionAllowed('SKIP') && (
            <Button
              color="inherit"
              type="button"
              endIcon={<ArrowForwardIcon style={{ fontSize: 16 }} />}
              style={{ marginRight: 8 }}
              onClick={(): unknown => send('SKIP')}
            >
              Skip
            </Button>
          )}
          <Button
            type="button"
            variant="contained"
            disabled={!isFormValid || isSubmitting}
            onClick={handleSubmit(onSubmit)}
          >
            {isSubmitting ? <CircularProgress size={21} /> : isFailed ? 'Try again' : 'Confirm'}
          </Button>
        </div>
      }
      header={<DeedHeartHands />}
      headerStyles={{
        paddingTop: 16,
      }}
    >
      <Box>
        <ModalTitle size="large">Welcome to Deed!</ModalTitle>
        <ModalTypography>
          Your home for corporate giving, volunteering, and relationship-building!
          <br />
          Let’s get you set up starting with user verification.
        </ModalTypography>
        <ModalTitle>Organization Verification</ModalTitle>
        <ModalTypography>
          To complete onboarding, please upload your 4076c Government Information Letter.
        </ModalTypography>
        <Controller
          control={control}
          name="governmentInformationLetterUri"
          render={({ field: { onChange } }) => (
            <FilesUpload
              height="sm"
              s3Acl="private"
              s3Bucket="secure"
              onChange={(files, uploadInProgress) => {
                const publicFileUrl = files?.[0]?.publicUrl || null;
                if (!uploadInProgress) {
                  onChange(publicFileUrl);
                }
              }}
              multiple={false}
            />
          )}
        />

        <ModalTypography style={{ marginTop: 32 }}>
          If your organization is a department of legal entity listed above,
          <br />
          please also provide a letter of association. (if necessary)
        </ModalTypography>
        <Controller
          control={control}
          name="letterOfAssociationUri"
          render={({ field: { onChange } }) => (
            <FilesUpload
              height="sm"
              s3Acl="private"
              s3Bucket="secure"
              onChange={(files, uploadInProgress) => {
                const publicFileUrl = files?.[0]?.publicUrl || null;
                if (!uploadInProgress) {
                  onChange(publicFileUrl);
                }
              }}
              multiple={false}
            />
          )}
        />

        <Controller
          control={control}
          name="legalName"
          rules={{
            required: "Please provide the entity's legal name",
          }}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              style={{ marginTop: 32 }}
              helperText={
                errors.legalName?.message ||
                'Please enter your legal name as its stated in your 4076c Government Information Letter'
              }
              error={Boolean(errors.legalName)}
              required
              label="Legal Name"
              fullWidth
              variant="standard"
            />
          )}
        />

        {isFailed && (
          <Box mt={2}>
            <Alert severity="error">Failed to submit, please try again</Alert>
          </Box>
        )}
      </Box>
    </ModalStep>
  );
};
